import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { db } from "integrations/firebase/firestore";

// No firebase/auth import anywhere in this module, deliberately. AppShell reads
// useSession, so anything imported here is pulled into every component that
// renders the shell — and integrations/firebase/auth calls getAuth() at module
// load, which throws without a real API key. SessionBoundary, which does need
// auth, lives in session-boundary.tsx for exactly that reason.
export type PlatformRole = "user" | "admin" | "super_admin";

/**
 * What the chrome needs to know about who is signed in — as opposed to what a
 * page needs, which each page still loads for itself.
 *
 * It exists because the sidebar is rendered by every page but owned by none of
 * them, so the alternative is drilling the same two facts through seven feature
 * components. Entitlement only, never authorization: firestore.rules decides
 * what may be read, and each gated route re-checks before it renders.
 */
export interface Session {
  uid: string;
  email: string | null;
  /** null until the profile read lands, and for a user still being provisioned. */
  platformRole: PlatformRole | null;
  administersAnyEvent: boolean;
}

// null outside a provider, and null while signed out — the sign-in screen and
// component tests render the shell without a session, and a shell with nobody
// signed in advertises nothing role-gated.
const SessionContext = createContext<Session | null>(null);

export const SessionProvider = SessionContext.Provider;

export function useSession(): Session | null {
  return useContext(SessionContext);
}

const ROLES: readonly string[] = ["user", "admin", "super_admin"];

/**
 * Loads the two entitlement facts, or null when nobody is signed in.
 *
 * Deliberately not `useUserProfile`: that hook polls for up to 7.5s waiting on
 * provisioning, which is right for the profile page and wrong for chrome that
 * renders on every route.
 *
 * Failures degrade to "no entitlements" rather than surfacing. A user who
 * cannot read their own profile has bigger problems than a missing nav item,
 * and the page behind each item reports the real error.
 */
export function useSessionFacts(
  uid: string | null,
  email: string | null,
): Session | null {
  const [platformRole, setPlatformRole] = useState<PlatformRole | null>(null);
  const [administersAnyEvent, setAdministersAnyEvent] = useState(false);
  // Bumped when the window regains focus. Both facts change without a new
  // sign-in: a super admin grants a role or makes someone an event admin, and
  // the person finds out in another tab. Without a re-read the Administration
  // item stayed hidden until they signed in again or reloaded.
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (uid === null) return;
    const onFocus = () => setRefresh((n) => n + 1);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [uid]);

  // Drop the previous user's entitlements before reading the new one's.
  // Firebase can move from one signed-in user straight to another without a
  // null in between, and every branch below is conditional — so without this
  // reset a denied read or an unprovisioned profile would leave the OLD role
  // on screen indefinitely rather than for one round trip. Keyed on uid alone,
  // so a focus refresh keeps the current facts on screen while it re-reads.
  useEffect(() => {
    setPlatformRole(null);
    setAdministersAnyEvent(false);
  }, [uid]);

  useEffect(() => {
    let cancelled = false;
    if (uid === null) return;

    async function load(signedInUid: string) {
      const [profile, memberships] = await Promise.allSettled([
        getDoc(doc(db, "users", signedInUid)),
        getDocs(
          query(
            collectionGroup(db, "members"),
            where("uid", "==", signedInUid),
          ),
        ),
      ]);
      if (cancelled) return;

      if (profile.status === "fulfilled" && profile.value.exists()) {
        const role: unknown = profile.value.data()?.platformRole;
        setPlatformRole(
          typeof role === "string" && ROLES.includes(role)
            ? (role as PlatformRole)
            : null,
        );
      }

      if (memberships.status === "fulfilled") {
        setAdministersAnyEvent(
          memberships.value.docs.some((d) => d.data()?.isEventAdmin === true),
        );
      }
    }

    void load(uid);
    return () => {
      cancelled = true;
    };
  }, [uid, refresh]);

  // Memoised because this is a context value: a fresh object every render would
  // re-render every consumer on every parent render.
  return useMemo(
    () =>
      uid === null ? null : { uid, email, platformRole, administersAnyEvent },
    [uid, email, platformRole, administersAnyEvent],
  );
}
